import { useState, useCallback, createRef, RefObject } from 'react';
import core, { View } from '@gin-betting/core';
import Scrollbars, { positionValues } from 'react-custom-scrollbars';

import { useMemoSelector } from 'hooks';
import { selectFilteredScores } from 'selectors';
import { MatchPartInfo } from 'types';

interface Input {
    matchId: string;
}

interface Output {
    server: string | null;
    scores: View.ScoreInfo[];
    scrollBarsRef: RefObject<Scrollbars>;
    isArrowRotated: boolean;
    onScrollUpdate: (values: positionValues) => void;
    onArrowClick: () => void;
    currentMatchPart: MatchPartInfo | null;
}

const {
    entities: {
        selectors: { selectMatchMetaValueByName },
    },
} = core;

function usePromoMatchDynamicScoreList({ matchId }: Input): Output {
    const [isArrowRotated, changeIsArrowRotated] = useState(false);

    const { scores, currentMatchPart } = useMemoSelector(selectFilteredScores, {
        matchId,
    });
    const server = useMemoSelector(selectMatchMetaValueByName, {
        matchId,
        metaName: 'server',
    });

    const scrollBarsRef = createRef<Scrollbars>();

    const onArrowClick = useCallback(() => {
        if (!scrollBarsRef.current) return;

        const {
            getValues,
            scrollToBottom,
            scrollToTop,
        } = scrollBarsRef.current;

        const values = getValues();

        if (values.top > 0.5) {
            scrollToTop();
            changeIsArrowRotated(false);
        } else {
            scrollToBottom();
            changeIsArrowRotated(true);
        }
    }, [scrollBarsRef]);

    const onScrollUpdate = useCallback((values: positionValues) => {
        changeIsArrowRotated(values.top > 0.5);
    }, []);

    return {
        server,
        scores,
        scrollBarsRef,
        isArrowRotated,
        onScrollUpdate,
        onArrowClick,
        currentMatchPart,
    };
}

export default usePromoMatchDynamicScoreList;
